import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations } from '../i18n/translations';

export const LanguageContext = createContext();

const STORAGE_KEY = 'portfolio_language';

export const SUPPORTED_LANGUAGES = ['en', 'zh', 'vi'];

const HTML_LANG = {
  en: 'en',
  zh: 'zh-CN',
  vi: 'vi',
};

const isSupported = (lang) => SUPPORTED_LANGUAGES.includes(lang);

// The Chinese webfont is large, so it loads only when Chinese is selected.
const ZH_FONT_HREF = 'https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@300;400;500;600;700;800;900&display=swap';

const loadChineseFont = () => {
  if (typeof document === 'undefined' || document.getElementById('font-noto-sans-sc')) return;
  const link = document.createElement('link');
  link.id = 'font-noto-sans-sc';
  link.rel = 'stylesheet';
  link.href = ZH_FONT_HREF;
  document.head.appendChild(link);
};

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (isSupported(saved)) {
          return saved;
        }
      } catch (e) {
        // Storage unavailable (private mode, blocked site data)
      }
    }
    return 'en'; // Default to English as requested
  });

  const setLanguage = useCallback((newLang) => {
    if (isSupported(newLang)) {
      setLanguageState(newLang);
      try {
        localStorage.setItem(STORAGE_KEY, newLang);
      } catch (e) {
        console.warn('Unable to persist language preference', e);
      }
    }
  }, []);

  // Cycle through supported languages (en -> zh -> vi -> en)
  const toggleLanguage = useCallback(() => {
    const index = SUPPORTED_LANGUAGES.indexOf(language);
    setLanguage(SUPPORTED_LANGUAGES[(index + 1) % SUPPORTED_LANGUAGES.length]);
  }, [language, setLanguage]);

  // Synchronize document attributes and font styling
  useEffect(() => {
    if (typeof document !== 'undefined') {
      if (language === 'zh') loadChineseFont();
      document.documentElement.lang = HTML_LANG[language] || 'en';
      SUPPORTED_LANGUAGES.forEach((code) => {
        if (code === 'en') return;
        document.documentElement.classList.toggle(`lang-${code}`, language === code);
        document.body.classList.toggle(`lang-${code}`, language === code);
      });
    }
  }, [language]);

  // Safe nested key lookup helper
  const tFunc = useCallback((path, fallback = '') => {
    if (!path) return fallback;
    const keys = path.split('.');
    let current = translations[language];

    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        // Fallback to English if key missing in current language
        let enCurrent = translations['en'];
        for (const enKey of keys) {
          if (enCurrent && typeof enCurrent === 'object' && enKey in enCurrent) {
            enCurrent = enCurrent[enKey];
          } else {
            return fallback || path;
          }
        }
        return enCurrent ?? fallback ?? path;
      }
    }
    return current ?? fallback ?? path;
  }, [language]);

  const currentTranslations = translations[language] || translations.en;
  const t = Object.assign((path, fallback) => tFunc(path, fallback), currentTranslations);

  const value = {
    language,
    isZh: language === 'zh',
    isVi: language === 'vi',
    setLanguage,
    toggleLanguage,
    t,
    strings: currentTranslations,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    const fallbackT = Object.assign(
      (path, fallback = '') => fallback || path,
      translations.en
    );
    return {
      language: 'en',
      isZh: false,
      isVi: false,
      setLanguage: () => {},
      toggleLanguage: () => {},
      t: fallbackT,
      strings: translations.en,
    };
  }
  return context;
}
